<template>
    <section class="dp-layout lost-found-config-page">
        <section class="dp-left">
            <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
        </section>

        <section class="dp-right">
            <template v-if="schoolId > 0">
                <div class="header-bar">
                    <div class="school-info">
                        <span class="label">当前学校：</span>
                        <span class="value">{{ schoolName || '未命名学校' }}</span>
                    </div>
                    <el-button v-if="hasActionPermission('handleSave')" type="primary" size="small" icon="el-icon-check" :loading="saving" @click="handleSave">
                        保存配置
                    </el-button>
                </div>

                <el-card class="config-card" shadow="never" v-loading="loading">
                    <div slot="header" class="card-header">
                        <i class="el-icon-setting" style="color: #409EFF; margin-right: 6px;"></i>
                        <span class="title">失物招领展示与点评配置</span>
                    </div>

                    <el-form :model="form" :rules="formRules" ref="configForm" label-position="top" size="small" class="config-form">
                        <div class="config-grid">
                            <section class="config-block">
                                <h4 class="config-block__title">展示</h4>
                                <el-form-item label="公示展示天数" prop="DisplayDays">
                                    <div class="control-line">
                                        <el-input-number v-model="form.DisplayDays" :min="1" :max="365" :step="1" controls-position="right" />
                                        <span class="unit-hint">天</span>
                                    </div>
                                    <p class="field-tip">H5 和班牌只展示公示期内的记录，默认 7 天。</p>
                                </el-form-item>

                                <el-form-item label="班牌最大展示条数" prop="CplateMaxCount">
                                    <div class="control-line">
                                        <el-input-number v-model="form.CplateMaxCount" :min="0" :max="999" :step="1" controls-position="right" />
                                        <span class="unit-hint">条</span>
                                    </div>
                                    <p class="field-tip">填 0 表示班牌展示全部公示中的记录。</p>
                                </el-form-item>

                                <el-form-item label="每生每日加分上限" prop="DailyEvalLimit">
                                    <div class="control-line">
                                        <el-input-number v-model="form.DailyEvalLimit" :min="0" :max="20" :step="1" controls-position="right" />
                                        <span class="unit-hint">次</span>
                                    </div>
                                    <p class="field-tip">按每位学生、每个自然日统计。超出后仍可发布，但不再加分。填 0 表示不限制。作废冲正后仍占用当天次数。</p>
                                </el-form-item>
                            </section>

                            <section class="config-block">
                                <h4 class="config-block__title">权限与点评</h4>
                                <el-form-item label="关联点评加分">
                                    <el-switch v-model="form.EvaluationEnabled" active-text="开启" inactive-text="关闭" @change="onEvaluationEnabledChange" />
                                    <p class="field-tip">开启后，发布时已选择捡拾学生才会写入表扬点评。未选择学生不加分。</p>
                                </el-form-item>

                                <el-form-item label="仅班主任可发布/核销">
                                    <el-switch v-model="form.OnlyClassTeacher" active-text="是" inactive-text="否" />
                                    <p class="field-tip">开启后仅班主任可发布和核销。关闭后本校任意教师均可操作。默认开启。</p>
                                </el-form-item>

                                <template v-if="form.EvaluationEnabled">
                                    <el-form-item label="表扬点评项" prop="EvaluationItemId">
                                        <el-cascader
                                            v-model="form.EvaluationItemId"
                                            :options="praiseTree"
                                            :props="cascaderProps"
                                            :show-all-levels="true"
                                            clearable
                                            filterable
                                            placeholder="请选择表扬点评项"
                                            class="eval-cascader"
                                            @change="onEvalItemChange"
                                        />
                                        <p v-if="form.EvaluationItemInvalid" class="warn-tip">配置已失效，请重新选择</p>
                                    </el-form-item>

                                    <el-form-item v-if="evalScoreIsMax" label="实际加分值" prop="EvaluationScore">
                                        <div class="control-line">
                                            <el-input-number
                                                v-model="form.EvaluationScore"
                                                :min="evalScoreMin"
                                                :max="evalItemScoreMax"
                                                :step="1"
                                                :precision="2"
                                                controls-position="right"
                                            />
                                            <span class="unit-hint">分，上限 {{ formatScore(evalItemScoreMax) }}</span>
                                        </div>
                                    </el-form-item>
                                </template>
                            </section>
                        </div>
                    </el-form>
                </el-card>
            </template>

            <div v-else class="no-department-tip">
                <div class="no-department-tip__inner">
                    <i class="el-icon-school"></i>
                    <p>请在左侧选择所属学校，查看与配置失物招领参数</p>
                </div>
            </div>
        </section>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import { getButtonList } from '../../promissionRouter';
import {
    getLostAndFoundConfig,
    getLostAndFoundPraiseItemTree,
    saveLostAndFoundConfig
} from '../../api/modules/lostAndFound';
import { normalizeDeptId, deptIdsEqual, resolveActiveSchoolId } from '../../utils/deptCascaderProps';

export default {
    name: 'LostAndFoundConfig',
    components: { DepartmentPane },
    data() {
        return {
            schoolId: 0,
            schoolName: '',
            boundSchoolId: 0,
            buttonList: [],
            loading: false,
            saving: false,
            praiseTree: [],
            form: {
                DisplayDays: 7,
                CplateMaxCount: 0,
                DailyEvalLimit: 1,
                OnlyClassTeacher: true,
                EvaluationEnabled: false,
                EvaluationItemId: null,
                EvaluationItemInvalid: false,
                EvaluationScoreMode: '',
                EvaluationItemScore: 0,
                EvaluationScore: null
            },
            formRules: {
                DisplayDays: [{ required: true, message: '请填写展示天数', trigger: 'blur' }],
                DailyEvalLimit: [{ required: true, message: '请填写每生每日加分上限', trigger: 'blur' }],
                EvaluationItemId: [{
                    validator: (rule, value, callback) => {
                        if (this.form.EvaluationEnabled && !value) {
                            callback(new Error('开启点评加分时必须选择表扬点评项'));
                        } else {
                            callback();
                        }
                    },
                    trigger: 'change'
                }],
                EvaluationScore: [{
                    validator: (rule, value, callback) => {
                        if (!this.form.EvaluationEnabled || !this.evalScoreIsMax) {
                            callback();
                            return;
                        }
                        const n = Number(value);
                        if (!n || n <= 0) {
                            callback(new Error('请填写有效的实际加分值'));
                        } else if (n > this.evalItemScoreMax) {
                            callback(new Error(`实际加分不能超过上限 ${this.evalItemScoreMax}`));
                        } else {
                            callback();
                        }
                    },
                    trigger: 'blur'
                }]
            },
            cascaderProps: {
                value: 'id',
                label: 'name',
                children: 'children',
                emitPath: false
            }
        };
    },
    computed: {
        evalSelectedLeaf() {
            return this.findLeaf(this.praiseTree, this.form.EvaluationItemId);
        },
        evalScoreIsMax() {
            const leaf = this.evalSelectedLeaf;
            const mode = leaf
                ? (leaf.scoreMode || leaf.ScoreMode)
                : this.form.EvaluationScoreMode;
            return String(mode || '').toLowerCase() === 'max';
        },
        evalItemScoreMax() {
            const leaf = this.evalSelectedLeaf;
            const raw = leaf
                ? (leaf.scoreValue != null ? leaf.scoreValue : leaf.ScoreValue)
                : this.form.EvaluationItemScore;
            const n = Number(raw);
            return n > 0 ? n : 0;
        },
        evalScoreMin() {
            return this.evalItemScoreMax >= 1 ? 1 : 0.01;
        }
    },
    watch: {
        '$store.state.activeSchoolId'(newSchoolId) {
            this.applySchoolChange(newSchoolId, this.$store.state.activeSchoolName);
        }
    },
    methods: {
        /** 判断当前页面是否配置了指定按钮权限 */
        hasActionPermission(func) {
            return this.buttonList.some(button => button.Func === func);
        },

        /** 解析当前学校 Id（字符串，避免雪花 Id 被 Number 截断）。最后修改：2026-10-18 */
        resolveSchoolId() {
            const fromStore = resolveActiveSchoolId(this);
            if (fromStore) return fromStore;
            const local = normalizeDeptId(this.schoolId);
            if (local) return local;
            return '';
        },

        /**
         * 顶栏切换学校或左侧学校树触发时统一换校加载。最后更新：2026-09-12
         */
        applySchoolChange(schoolId, schoolName) {
            const id = normalizeDeptId(schoolId);
            if (!id) return;
            if (deptIdsEqual(id, this.schoolId) && deptIdsEqual(this.boundSchoolId, id)) return;
            this.schoolId = id;
            this.boundSchoolId = id;
            if (schoolName) this.schoolName = schoolName;
            this.loadAll();
        },

        treeNodeClick(data) {
            this.applySchoolChange(data && data.value, data && data.label);
        },

        pick(obj, camel, pascal) {
            if (!obj) return undefined;
            return obj[camel] != null ? obj[camel] : obj[pascal];
        },

        findLeaf(nodes, id) {
            if (!id || !Array.isArray(nodes)) return null;
            const key = String(id);
            for (let i = 0; i < nodes.length; i++) {
                const n = nodes[i];
                if (String(n.id) === key && (!n.children || !n.children.length)) return n;
                const hit = this.findLeaf(n.children, id);
                if (hit) return hit;
            }
            return null;
        },

        formatScore(v) {
            const n = Number(v);
            if (Number.isNaN(n)) return '';
            return Number.isInteger(n) ? String(n) : String(n);
        },

        /** 规范化点评树节点，统一小写 id/name/children。最后更新：2026-09-12 */
        normalizeTree(nodes) {
            if (!Array.isArray(nodes)) return [];
            return nodes.map(n => {
                const childrenRaw = n.children || n.Children;
                const isLeaf = n.isLeaf != null ? n.isLeaf : n.IsLeaf;
                const children = this.normalizeTree(childrenRaw);
                return {
                    id: String(n.id != null ? n.id : n.Id),
                    name: n.name != null ? n.name : n.Name,
                    scoreMode: n.scoreMode || n.ScoreMode || '',
                    scoreValue: n.scoreValue != null ? n.scoreValue : n.ScoreValue,
                    children: isLeaf || !children.length ? undefined : children
                };
            });
        },

        onEvaluationEnabledChange(val) {
            if (!val) {
                this.form.EvaluationItemInvalid = false;
            }
            this.$nextTick(() => this.$refs.configForm && this.$refs.configForm.clearValidate());
        },

        onEvalItemChange() {
            this.form.EvaluationItemInvalid = false;
            if (!this.evalScoreIsMax) {
                this.form.EvaluationScore = null;
                this.form.EvaluationScoreMode = '';
                this.form.EvaluationItemScore = 0;
                return;
            }
            this.form.EvaluationScoreMode = 'Max';
            this.form.EvaluationItemScore = this.evalItemScoreMax;
            const cur = Number(this.form.EvaluationScore);
            if (!cur || cur > this.evalItemScoreMax) {
                this.form.EvaluationScore = this.evalItemScoreMax;
            }
        },

        resetForm() {
            this.form = {
                DisplayDays: 7,
                CplateMaxCount: 0,
                DailyEvalLimit: 1,
                EvaluationEnabled: false,
                EvaluationItemId: null,
                EvaluationItemInvalid: false,
                EvaluationScoreMode: '',
                EvaluationItemScore: 0,
                EvaluationScore: null
            };
            this.praiseTree = [];
        },

        async loadAll() {
            if (!this.schoolId) return;
            this.loading = true;
            try {
                await Promise.all([this.loadConfig(), this.loadPraiseTree()]);
            } finally {
                this.loading = false;
            }
        },

        async loadPraiseTree() {
            try {
                const res = await getLostAndFoundPraiseItemTree({ schoolId: this.schoolId });
                const list = res.data && res.data.response ? res.data.response : [];
                this.praiseTree = this.normalizeTree(list);
            } catch (err) {
                this.praiseTree = [];
            }
        },

        async loadConfig() {
            try {
                const res = await getLostAndFoundConfig({ schoolId: this.schoolId });
                if (!(res.data && res.data.success)) {
                    this.resetForm();
                    return;
                }
                const r = res.data.response || {};
                const itemId = this.pick(r, 'evaluationItemId', 'EvaluationItemId');
                this.form = {
                    DisplayDays: Number(this.pick(r, 'displayDays', 'DisplayDays')) || 7,
                    CplateMaxCount: Number(this.pick(r, 'cplateMaxCount', 'CplateMaxCount')) || 0,
                    DailyEvalLimit: (() => {
                        const v = this.pick(r, 'dailyEvalLimit', 'DailyEvalLimit');
                        return v == null || v === '' ? 1 : Number(v);
                    })(),
                    OnlyClassTeacher: this.pick(r, 'onlyClassTeacher', 'OnlyClassTeacher') !== false
                        && this.pick(r, 'onlyClassTeacher', 'OnlyClassTeacher') !== 0
                        && this.pick(r, 'onlyClassTeacher', 'OnlyClassTeacher') !== '0',
                    EvaluationEnabled: !!this.pick(r, 'evaluationEnabled', 'EvaluationEnabled'),
                    EvaluationItemId: itemId ? String(itemId) : null,
                    EvaluationItemInvalid: !!this.pick(r, 'evaluationItemInvalid', 'EvaluationItemInvalid'),
                    EvaluationScoreMode: this.pick(r, 'evaluationScoreMode', 'EvaluationScoreMode') || '',
                    EvaluationItemScore: Number(this.pick(r, 'evaluationItemScore', 'EvaluationItemScore')) || 0,
                    EvaluationScore: this.pick(r, 'evaluationScore', 'EvaluationScore')
                };
            } catch (err) {
                this.$message.error('加载失物招领配置失败');
                this.resetForm();
            }
        },

        handleSave() {
            this.$refs.configForm.validate(async (valid) => {
                if (!valid) return;
                this.saving = true;
                try {
                    const payload = {
                        SchoolId: this.schoolId,
                        DisplayDays: this.form.DisplayDays,
                        CplateMaxCount: this.form.CplateMaxCount,
                        DailyEvalLimit: this.form.DailyEvalLimit,
                        OnlyClassTeacher: this.form.OnlyClassTeacher,
                        EvaluationEnabled: this.form.EvaluationEnabled,
                        EvaluationItemId: this.form.EvaluationEnabled
                            ? (this.form.EvaluationItemId ? Number(this.form.EvaluationItemId) : null)
                            : 0,
                        EvaluationScore: this.form.EvaluationEnabled && this.evalScoreIsMax
                            ? this.form.EvaluationScore
                            : null
                    };
                    const res = await saveLostAndFoundConfig(payload);
                    if (res.data && res.data.success) {
                        this.$message.success('保存成功');
                        this.loadConfig();
                    } else {
                        this.$message.error(res.data ? res.data.msg : '保存失败');
                    }
                } catch (err) {
                    this.$message.error('保存异常，请重试');
                } finally {
                    this.saving = false;
                }
            });
        }
    },
    mounted() {
        const routers = window.localStorage.router ? JSON.parse(window.localStorage.router) : [];
        this.buttonList = getButtonList(this.$route.path, routers);
        // 学校范围页由 DepartmentPane 初始化时自动 emit tree-node-click，避免与此处重复请求
    }
};
</script>

<style scoped>
.header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    flex-shrink: 0;
}

.school-info .label {
    color: #909399;
    font-size: 13px;
}

.school-info .value {
    font-weight: 600;
    color: #303133;
    font-size: 15px;
}

.config-card {
    flex: 1;
    overflow: auto;
}

.card-header {
    display: flex;
    align-items: center;
}

.card-header .title {
    font-weight: 600;
    font-size: 15px;
}

.config-form {
    padding-top: 4px;
}

.config-grid {
    display: grid;
    grid-template-columns: minmax(280px, 1fr) minmax(280px, 1fr);
    gap: 16px;
}

.config-block {
    min-width: 0;
    padding: 16px 18px 4px;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    background: #fafbfc;
}

.config-block__title {
    margin: 0 0 14px;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
}

.control-line {
    display: flex;
    align-items: center;
    gap: 8px;
}

.unit-hint {
    color: #606266;
    font-size: 13px;
}

.field-tip {
    margin: 8px 0 0;
    color: #606266;
    font-size: 12px;
    line-height: 1.6;
}

.eval-cascader {
    width: 100%;
    max-width: 420px;
}

@media (max-width: 1100px) {
    .config-grid {
        grid-template-columns: 1fr;
    }
}

.warn-tip {
    margin: 6px 0 0;
    color: #E6A23C;
    font-size: 12px;
}

.no-department-tip {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 200px;
    color: #909399;
}

.no-department-tip i {
    font-size: 48px;
    margin-bottom: 12px;
    color: #c0c4cc;
}
</style>
